import React, { useState } from 'react';
import { Box, Typography, Divider, Avatar, useMediaQuery, useTheme, IconButton } from '@mui/material';
import { Images } from '../../utils/Images';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import StatCards from '../../components/common/MyProfile/StatCards';

const ProfileSidebar = ({ onItemClick, activeItem = 'My Profile' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery('(min-width:600px) and (max-width:1280px)'); 
  const [showMenu, setShowMenu] = useState(false);
  const navigate = useNavigate();

  const menuItems = [
    { id: 'My Profile', icon: Images.profileUser, label: 'My Profile' },
    { id: 'My Transactions', icon: Images.profileCard, label: 'My Transactions' },
    { id: 'Donate Once', icon: Images.profileCoin, label: 'Donate Once' },
    { id: 'Donate Monthly', icon: Images.profileWalletMoney, label: 'Donate Monthly' },
    { id: 'Overseas Donation', icon: Images.profileCoin, label: 'Overseas Donation' },
    { id: 'Download 80G Form', icon: Images.profileDocumentDownload, label: 'Download 80G Form' },
    { id: 'Download 10B Form', icon: Images.profileDocumentDownload, label: 'Download 10B Form' },
    { id: 'My Subscriptions', icon: Images.profileCrown, label: 'My Subscriptions' },
    { id: 'Logout', icon: Images.profileLogout, label: 'Logout' },
  ];

  const handleItemClick = (itemId) => {
    // This ensures we navigate to a new page when clicking an option
    if (onItemClick) {
      onItemClick(itemId);
    }
    
    // For mobile, after clicking an option, we should immediately navigate
    // This avoids having to click twice
    if (isMobile) {
      if (itemId === 'My Profile') {
        navigate('/profile/my-profile');
      } else if (itemId === 'Donate Once') {
        navigate('/profile/donate-once');
      } else if (itemId === 'Donate Monthly') {
        navigate('/profile/donate-monthly');
      } else if (itemId === 'Overseas Donation') {
        navigate('/profile/overseas-donation');
      } else if (itemId === 'My Transactions') {
        navigate('/profile/my-transactions');
      } else if (itemId === 'My Subscriptions') {
        navigate('/profile/subscriptions');
      } else if (itemId === 'Download 80G Form') {
        navigate('/profile/80g-forms');
      } else if (itemId === 'Download 10B Form') {
        navigate('/profile/10b-forms');
      } else if (itemId === 'Logout') {
        // Logout is handled by the parent component
      }
    }
  };

  const handleBackClick = () => {
    navigate('/');
  };

  // Get user data from Redux store
  const { user } = useSelector((state) => state.auth);
  const userData = {
    name: user?.name || "User",
    role: "Donor",
    id: user?.id || "N/A",
    profilePicture: user?.profilePicture || null,
  };

  if (isMobile) {
    return (
      <Box sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Header with back button and page title */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          marginBottom: '30px',
          marginTop: '30px',
          justifyContent: 'center',
        }}>
          <Box 
            onClick={handleBackClick}
            sx={{ 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              position: 'absolute',
              left: 0
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="#1D1D1B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Box>
          <Typography sx={{
            // fontFamily: 'Plus Jakarta Sans',
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '100%',
            letterSpacing: '0%',
            color: '#1D1D1B'
          }}>
            My Account
          </Typography>
        </Box>

        {/* User profile section */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '20px',
          gap: '15px',
          borderRadius: '10px',
          background: '#FFFFFF',
          boxShadow: '0px 4px 20px 0px #00000026',
          padding: '16px 6px 16px 16px',
          position: 'relative'
        }}>
          <Avatar 
            src={userData.profilePicture || Images.defaultAvatar} 
            sx={{ width: 56, height: 56 }}
          />
          <Box sx={{ flex: 1, minWidth: 0, pr: '88px' }}>
            <Typography sx={{
              fontWeight: 600,
              fontSize: '16px',
              color: '#1D1D1B',
              marginBottom: '4px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {userData.name}
            </Typography>
            <Typography sx={{
              fontSize: '14px',
              color: '#5E5E5E',
              fontWeight: 400
            }}>
              {userData.role}
            </Typography>
          </Box>
          <Box sx={{
            position: 'absolute',
            right: '16px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: 'fit-content',
            maxWidth: 'calc(100% - 88px)',
            height: '22px',
            borderRadius: '5px',
            padding: '4px 8px',
            background: '#29CC6A',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexShrink: 0,
          }}>
            <Typography sx={{
              fontFamily: 'Red Hat Display',
              fontWeight: 700,
              fontSize: '10px',
              lineHeight: '14px',
              letterSpacing: '0%',
              color: '#FFFFFF',
              whiteSpace: 'nowrap',
            }}>
              ID {userData.id}
            </Typography>
          </Box>
        </Box>

        {/* Stats section */}
        <Box sx={{ marginBottom: '20px' }}>
          <StatCards user={user} />
        </Box>

        {/* Menu options */}
        <Box sx={{
          borderRadius: '10px',
          padding: '20px',
          background: '#FFFFFF',
          boxShadow: '0px 4px 20px 0px #00000026',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px'
        }}>
          {menuItems.map((item, index) => (
            <React.Fragment key={item.id}>
              <Box
                onClick={() => handleItemClick(item.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 0',
                  cursor: 'pointer'
                }}
              >
                <Box
                  component="img"
                  src={item.icon}
                  alt={item.label}
                  sx={{
                    width: '18px',
                    height: '18px',
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: 'Red Hat Display',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '100%',
                    letterSpacing: '0%',
                    verticalAlign: 'middle',
                    color: '#1D1D1B',
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
              {index < menuItems.length - 1 && (
                <Divider sx={{ width: '100%' }} />
              )}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    );
  }

  // Tablet version (1024px to 600px)
  if (isTablet) {
    return (
      <Box sx={{
        position: 'relative',
        zIndex: 1000,
      }}>
        {/* Menu Button with Text */}
        <Box
          onClick={() => setShowMenu(!showMenu)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            cursor: 'pointer',
          }}
        >
          <IconButton 
            sx={{
              backgroundColor: '#1D1D1B',
              borderRadius: '8px',
              width: '48px',
              height: '48px',
              '&:hover': {
                backgroundColor: '#333',
              }
            }}
          >
            {showMenu ? (
              // X icon when menu is open
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              // Hamburger menu icon when closed
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 12H21M3 6H21M3 18H21" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </IconButton>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '16px',
              color: '#1D1D1B',
            }}
          >
            {showMenu ? 'Close' : 'Profile Menu'}
          </Typography>
        </Box>
        
        {/* Collapsible Menu */}
        {showMenu && (
          <Box
            sx={{
              position: 'absolute',
              top: '56px',
              left: 0,
              width: '218px',
              borderRadius: '12px',
              border: '1px solid #0000001A',
              padding: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              backgroundColor: '#FFFFFF',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
              zIndex: 1000,
            }}
          >
            {menuItems.map((item) => (
              <Box
                key={item.id}
                onClick={() => {
                  handleItemClick(item.id);
                  setShowMenu(false); // Close menu after selection
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  height: '47px',
                  borderRadius: '8px',
                  paddingTop: '14px',
                  paddingBottom: '14px',
                  paddingLeft: '16px',
                  cursor: 'pointer',
                  backgroundColor: activeItem === item.id ? '#1D1D1B' : 'transparent',
                  transition: 'all 0.2s ease-in-out',
                  opacity: 1,
                  '&:hover': {
                    backgroundColor: activeItem === item.id ? '#1D1D1B' : 'rgba(29, 29, 27, 0.05)',
                  },
                }}
              >
                <Box
                  component="img"
                  src={item.icon}
                  alt={item.label}
                  sx={{
                    width: '18px',
                    height: '18px',
                    filter: activeItem === item.id ? 'brightness(0) invert(1)' : 'brightness(0) saturate(100%) invert(36%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(95%) contrast(88%)',
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: activeItem === item.id ? 'inherit' : 'Red Hat Display',
                    fontWeight: 500,
                    fontStyle: 'Medium',
                    fontSize: '14px',
                    lineHeight: '100%',
                    letterSpacing: '0%',
                    verticalAlign: 'middle',
                    color: activeItem === item.id ? '#FFFFFF' : '#5E5E5E',
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  }

  // Desktop version remains unchanged
  return (
    <Box sx={{
      width: '218px',
      height: '530px',
      borderRadius: '12px',
      border: '1px solid #0000001A',
      padding: '12px',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      backgroundColor: '#FFFFFF',
    }}>
      {menuItems.map((item) => (
        <Box
          key={item.id}
          onClick={() => handleItemClick(item.id)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            height: '47px',
            borderRadius: '8px',
            paddingTop: '14px',
            paddingBottom: '14px',
            paddingLeft: '16px',
            cursor: 'pointer',
            backgroundColor: activeItem === item.id ? '#1D1D1B' : 'transparent',
            transition: 'all 0.2s ease-in-out',
            opacity: 1,
            '&:hover': {
              backgroundColor: activeItem === item.id ? '#1D1D1B' : 'rgba(29, 29, 27, 0.05)',
            },
          }}
        >
          <Box
            component="img"
            src={item.icon}
            alt={item.label}
            sx={{
              width: '18px',
              height: '18px',
              filter: activeItem === item.id ? 'brightness(0) invert(1)' : 'brightness(0) saturate(100%) invert(36%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(95%) contrast(88%)',
            }}
          />
          <Typography
            variant="body2"
            sx={{
              fontFamily: activeItem === item.id ? 'inherit' : 'Red Hat Display',
              fontWeight: 500,
              fontStyle: 'Medium',
              fontSize: '14px',
              lineHeight: '100%',
              letterSpacing: '0%',
              verticalAlign: 'middle',
              color: activeItem === item.id ? '#FFFFFF' : '#5E5E5E',
            }}
          >
            {item.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default ProfileSidebar;
